import React, { useState } from 'react'
import { CollateralBasketType, PlatformFeature, Product, ProductSector, RepoType, ValidationIssue } from 'api'
import styled from '@emotion/styled'
import { Locale, Translation } from 'locale'
import { isEmpty, isNil } from 'lodash'
import { getEnumValues, RepoTypeHelper } from 'model'
import * as constants from 'view/styles/constants'
import { RadioButtonStripField, TextButton, TextButtonType } from 'view/components/buttons'
import { Groupbox, ModalFooter, ModalWindow } from 'view/components/panels'
import { DialogField, DialogRow, NumericInputField, TextInputField, TimePickerField } from 'view/components/inputs'
import { SelectboxField } from 'view/components/options'
import {
  ProductSelectboxField,
  CollateralBasketTypeSelectboxField,
  CurrencySelectboxField,
  RepoTypeMultiComboboxField,
  BooleanSelectboxField,
  ReferenceRateMultiComboboxField,
  ExDividendDayConventionSelectboxField
} from 'view/shared/entity'
import { FeatureToggle } from 'view/shell/FeatureToggle'
import moment from 'moment'
import { sliceFieldName } from 'utils'
import { useUpdateHandler } from 'view/hooks'
import { sectorStatusTranslationMap, sectorStatusTranslationReverseMap } from '../utils'

interface SectorDialogComponentProps {
  title: string
  productSector: ProductSector
  getProduct: (productId: string) => Product | null
  readOnly: Partial<{ [T in keyof ProductSector]: boolean } & { isExDividend: boolean }>
  validationIssues: ValidationIssue[]
  onSave: () => void
  onClose: () => void
  onUpdate: (partialProductSector: Partial<ProductSector>) => void
}

const Content = styled(Groupbox)`
  padding: 20px 20px 0 20px;
  min-height: 250px;
  overflow-y: auto;
  width: 650px;
`

const DEFAULT_CRYSTALLISATION_RATE = 2

export const SectorDialogComponent: React.FC<SectorDialogComponentProps> = ({
  title,
  productSector,
  readOnly,
  validationIssues,
  onClose,
  onSave,
  onUpdate
}) => {
  const [isExDividend, setExDividend] = useState<boolean | null>(!!(productSector.exDividendDays && productSector.exDividendDayConvention))
  const handleSetExDividend = (flag: boolean) => {
    if (!flag) {
      onUpdate({ exDividendDayConvention: null, exDividendDays: null })
    }
    setExDividend(flag)
  }

  const updateHandler = useUpdateHandler(onUpdate)

  const updateSupportedRepoTypes = (supportedRepoTypes: RepoType[]) => {
    const data: Partial<ProductSector> = {
      ...(!supportedRepoTypes.includes(productSector.defaultRepoType) && { defaultRepoType: (null as unknown) as RepoType }),
      supportedRepoTypes
    }
    onUpdate(data)
  }

  const updateDefaultNotificationDeadlineTime = (dateTime: Date | null) => {
    onUpdate({
      defaultNotificationDeadlineTime: dateTime ? moment(dateTime).format('HH:mm:ss') : null
    })
  }

  const onUpdateBasketType = (basketType: CollateralBasketType | null) => {
    onUpdate({
      basketType,
      basketCurrency: null,
      exDividendDays: null,
      exDividendDayConvention: null,
      defaultFloatCrystallisationDaysByReferenceRateId: undefined
    })

    setExDividend(false)
  }

  const onUpdateCrystallisationRate = (values: string[], rate = DEFAULT_CRYSTALLISATION_RATE) => {
    onUpdate({
      defaultFloatCrystallisationDaysByReferenceRateId: values.reduce((rates, key) => ({ ...rates, [key]: rate }), {})
    })
  }

  const defaultNotificationDeadlineTime = productSector.defaultNotificationDeadlineTime
    ? moment(productSector.defaultNotificationDeadlineTime, 'hh:mm:ss').toDate()
    : null

  const transformedReferenceRateValidationIssues =
    validationIssues.length > 0 ? validationIssues.map(_ => ({ ..._, fieldName: sliceFieldName(_.fieldName, -1) })) : []

  const selectedReferenceRates = Object.keys(productSector.defaultFloatCrystallisationDaysByReferenceRateId) || []
  const firstCrystallisationRate = productSector.defaultFloatCrystallisationDaysByReferenceRateId[selectedReferenceRates[0]]

  if (!productSector) {
    return null
  }

  const statusUpdateHandler = (value: Translation.TC_ENABLED | Translation.TC_DISABLED | Translation.TC_DELETED) => {
    updateHandler('status')(sectorStatusTranslationReverseMap[value])
  }

  return (
    <ModalWindow title={title}>
      <Content>
        <DialogRow>
          <DialogField flex={0}>
            <RadioButtonStripField
              dataTestId={'SectorDialog-TextInputField-SectorStatus'}
              label={Locale.translate(Translation.TC_SECTOR_STATUS)}
              fieldName="status"
              validationIssues={validationIssues}
              options={[Translation.TC_ENABLED, Translation.TC_DELETED]}
              selectedOption={sectorStatusTranslationMap[productSector.status]}
              buttonWidth={80}
              disabled={readOnly.status}
              onUpdate={statusUpdateHandler}
            />
          </DialogField>
        </DialogRow>
        <DialogRow>
          <DialogField>
            <TextInputField
              dataTestId={'SectorDialog-TextInputField-SectorName'}
              fieldName="name"
              label={Locale.translate(Translation.TC_SECTOR_NAME)}
              value={productSector.name}
              maxLength={constants.defaultMaxTextFieldLength}
              readOnly={readOnly.name}
              onUpdate={updateHandler('name')}
              validationIssues={validationIssues || undefined}
            />
          </DialogField>
          <DialogField>
            <ProductSelectboxField
              dataTestId={'SectorDialog-ProductSelectboxField-Product'}
              fieldName="productId"
              label={Locale.translate(Translation.TC_PRODUCT)}
              value={productSector.productId}
              readOnly={readOnly.productId}
              onUpdate={updateHandler('productId')}
              validationIssues={validationIssues || undefined}
            />
          </DialogField>
          <DialogField>
            <SelectboxField
              dataTestId={'SectorDialog-SelectboxField-BasisConvention'}
              value={productSector.basisConvention}
              label={Locale.translate(Translation.TC_BASIS_CONVENTION)}
              optionKeys={['ACT/360', 'ACT/365']}
              onUpdate={updateHandler('basisConvention')}
              readOnly={readOnly.basisConvention}
              validationIssues={validationIssues || undefined}
              fieldName={'basisConvention'}
            />
          </DialogField>
        </DialogRow>
        <DialogRow>
          <DialogField>
            <CollateralBasketTypeSelectboxField
              canClear
              dataTestId={'SectorDialog-CollateralBasketTypeSelectboxField-BasketType'}
              fieldName="basketType"
              label={Locale.translate(Translation.TC_BASKET_TYPE)}
              value={productSector.basketType}
              onUpdate={onUpdateBasketType}
              readOnly={readOnly.basketType}
              validationIssues={validationIssues || undefined}
            />
          </DialogField>
          <DialogField>
            <CurrencySelectboxField
              dataTestId={'SectorDialog-TextInputField-BasketCurrency'}
              fieldName={'basketCurrency'}
              label={Locale.translate(Translation.TC_BASKET_CURRENCY)}
              value={productSector.basketCurrency}
              onUpdate={updateHandler('basketCurrency')}
              readOnly={readOnly.basketCurrency}
              validationIssues={validationIssues || undefined}
            />
          </DialogField>
        </DialogRow>
        <DialogRow>
          <DialogField>
            <RepoTypeMultiComboboxField
              dataTestId={'SectorDialog-RepoTypeMultiComboboxField-SupportedRepoTypes'}
              label={Locale.translate(Translation.TC_SUPPORT_REPO_TYPES)}
              value={productSector.supportedRepoTypes || []}
              readOnly={readOnly.supportedRepoTypes}
              canClear={!readOnly.supportedRepoTypes}
              fieldName={'supportedRepoTypes'}
              onUpdate={updateSupportedRepoTypes}
              validationIssues={validationIssues || undefined}
              optionKeys={
                isNil(productSector.basketType) || [CollateralBasketType.GC_BESPOKE].includes(productSector.basketType)
                  ? getEnumValues(RepoType)
                  : getEnumValues(RepoType).filter(repoType => repoType !== RepoType.STOCK_BORROWING_LENDING)
              }
            />
          </DialogField>
          <DialogField>
            <SelectboxField
              dataTestId={'SectorDialog-SelectboxField-DefaultRepoType'}
              value={productSector.defaultRepoType}
              label={Locale.translate(Translation.TC_DEFAULT_REPO_TYPE)}
              optionKeys={productSector.supportedRepoTypes}
              getOptionText={RepoTypeHelper.getName}
              onUpdate={updateHandler('defaultRepoType')}
              readOnly={readOnly.defaultRepoType}
              fieldName={'defaultRepoType'}
              validationIssues={validationIssues || undefined}
            />
          </DialogField>
        </DialogRow>
        <DialogRow>
          <DialogField>
            <FeatureToggle feature={PlatformFeature.ENABLE_START_CONSIDERATION_OVERRIDE}>
              <NumericInputField
                dataTestId={'SectorDialog-NumericInputField-StartConsiderationOverrideThreshold'}
                fieldName="startConsiderationOverrideThreshold"
                label={Locale.translate(Translation.TC_START_CONSIDERATION_OVERRIDE_THRESHOLD)}
                value={productSector.startConsiderationOverrideThreshold}
                maxLength={constants.startConsiderationOverrideThresholdFieldLength}
                readOnly={readOnly.startConsiderationOverrideThreshold}
                onUpdate={updateHandler('startConsiderationOverrideThreshold')}
                validationIssues={validationIssues || undefined}
              />
            </FeatureToggle>
          </DialogField>
          <DialogField>
            <BooleanSelectboxField
              dataTestId={'SectorDialog-BooleanSelectboxField-isExDividend'}
              label={Locale.translate(Translation.SC_IS_EX_DIVIDEND)}
              value={isExDividend}
              readOnly={readOnly.isExDividend}
              onUpdate={handleSetExDividend}
            />
          </DialogField>
          <DialogField>
            <TimePickerField
              dataTestId={'SectorDialog-DatePickerField-DefaultNotificationDeadlineTime'}
              fieldName="defaultNotificationDeadlineTime"
              label={Locale.translate(Translation.TC_DEFAULT_NOTICE_TIME)}
              value={defaultNotificationDeadlineTime}
              readOnly={readOnly.defaultNotificationDeadlineTime}
              onUpdate={updateDefaultNotificationDeadlineTime}
              validationIssues={validationIssues || undefined}
            />
          </DialogField>
          <DialogField>
            <NumericInputField
              dataTestId={'SectorDialog-NumericInputField-DefaultNoticePeriod'}
              fieldName="defaultNoticePeriod"
              label={Locale.translate(Translation.TC_DEFAULT_NOTICE_PERIOD)}
              value={productSector.defaultNoticePeriod}
              maxLength={constants.sectorDefaultNoticePeriodFieldLength}
              readOnly={readOnly.defaultNoticePeriod}
              onUpdate={updateHandler('defaultNoticePeriod')}
              validationIssues={validationIssues || undefined}
            />
          </DialogField>
        </DialogRow>
        <DialogRow>
          <DialogField>
            <ReferenceRateMultiComboboxField
              dataTestId={'SectorDialog-ReferenceRateSelectboxField-ReferenceRate'}
              fieldName="defaultFloatCrystallisationDaysByReferenceRateId"
              label={Locale.translate(Translation.TC_REFERENCE_RATE)}
              value={selectedReferenceRates}
              readOnly={readOnly.defaultFloatCrystallisationDaysByReferenceRateId}
              onUpdate={(values: string[]) => onUpdateCrystallisationRate(values)}
              validationIssues={transformedReferenceRateValidationIssues}
            />
          </DialogField>
          <DialogField>
            <NumericInputField
              dataTestId={'SectorDialog-ReferenceRateSelectboxField-ReferenceRateDefault'}
              fieldName="defaultFloatCrystallisationDaysByReferenceRateId.ESTR"
              label={Locale.translate(Translation.TC_REFERENCE_RATE_DEFAULT_CRYSTALLISATION)}
              value={firstCrystallisationRate}
              readOnly={isEmpty(selectedReferenceRates) || readOnly.defaultFloatCrystallisationDaysByReferenceRateId}
              onUpdate={(rate: number) => {
                onUpdateCrystallisationRate(selectedReferenceRates, rate)
              }}
              validationIssues={transformedReferenceRateValidationIssues}
            />
          </DialogField>
          <DialogField>
            {isExDividend && (
              <ExDividendDayConventionSelectboxField
                dataTestId={'SectorDialog-ExDividendDayConventionSelectboxField-ExDividendDayConvention'}
                fieldName="exDividendDayConvention"
                label={Locale.translate(Translation.TC_EX_DIVIDEND_DAY_CONVENTION)}
                value={productSector.exDividendDayConvention}
                readOnly={readOnly.exDividendDayConvention}
                onUpdate={updateHandler('exDividendDayConvention')}
                validationIssues={validationIssues || undefined}
              />
            )}
          </DialogField>
          <DialogField>
            {isExDividend && (
              <NumericInputField
                dataTestId={'SectorDialog-NumericInputField-ExDividendDays'}
                fieldName="exDividendDays"
                label={Locale.translate(Translation.TC_EX_DIVIDEND_DAYS)}
                value={productSector.exDividendDays}
                maxLength={constants.sectorExDividendDaysFieldLength}
                readOnly={readOnly.exDividendDays}
                onUpdate={updateHandler('exDividendDays')}
                validationIssues={validationIssues || undefined}
              />
            )}
          </DialogField>
        </DialogRow>
      </Content>
      <ModalFooter>
        <DialogRow>
          <DialogField>
            <TextButton dataTestId={'SectorDialog-TextButton-CancelButton'} type={TextButtonType.Tertiary} onClick={onClose}>
              {Locale.translate(Translation.TC_BUTTON_CANCEL)}
            </TextButton>
          </DialogField>
          <DialogField flex={0}>
            <TextButton
              dataTestId={'SectorDialog-TextButton-SaveButton'}
              type={TextButtonType.Primary}
              disabled={readOnly.name || validationIssues.length > 0}
              onClick={onSave}>
              {Locale.translate(Translation.TC_BUTTON_SAVE)}
            </TextButton>
          </DialogField>
        </DialogRow>
      </ModalFooter>
    </ModalWindow>
  )
}
